import React, { useState, useEffect, useRef } from 'react';
import './Garden.css';

interface JumpToYearDialogProps {
  birthYear: number;
  currentYear: number;
  onClose: () => void;
  onJump: (year: number) => void;
}

const JumpToYearDialog: React.FC<JumpToYearDialogProps> = ({ birthYear, currentYear, onClose, onJump }) => {
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (isDropdownOpen) {
          setIsDropdownOpen(false);
        } else {
          onClose();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose, isDropdownOpen]);

  useEffect(() => {
    if (isDropdownOpen && listRef.current) {
      const selectedElement = listRef.current.querySelector(`[data-year="${selectedYear}"]`) as HTMLElement;
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [isDropdownOpen, selectedYear]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedYear >= birthYear && selectedYear <= currentYear) {
      onJump(selectedYear);
      onClose();
    }
  };

  const years = [];
  for (let year = birthYear; year <= currentYear; year++) {
    years.push(year);
  }

  const handleYearSelect = (year: number) => {
    setSelectedYear(year);
    setIsDropdownOpen(false);
  };

  const selectedYearLabel = `${selectedYear} (Age ${selectedYear - birthYear})`;

  return (
    <div className="garden-plant-modal-overlay">
      <div
        ref={modalRef}
        className="garden-plant-modal"
      >
        <div className="garden-plant-note-popover-header">
          <div className="garden-plant-note-popover-title">
            <span className="garden-plant-note-popover-plant-type">
              Jump to Year
            </span>
          </div>
          <button
            className="garden-plant-note-popover-close"
            onClick={onClose}
            title="Close"
          >
            ×
          </button>
        </div>

        <div className="garden-plant-note-popover-view">
          <div className="garden-plant-note-popover-content">
            <form onSubmit={handleSubmit} className="garden-jump-year-form">
              <div className="garden-jump-year-field">
                <label htmlFor="year-select" className="garden-jump-year-label">
                  Select Year
                </label>
                <div className="garden-jump-year-dropdown-container" ref={dropdownRef}>
                  <button
                    type="button"
                    id="year-select"
                    className="garden-jump-year-select"
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    aria-expanded={isDropdownOpen}
                    aria-haspopup="listbox"
                  >
                    <span>{selectedYearLabel}</span>
                    <span className="garden-jump-year-chevron">{isDropdownOpen ? '▲' : '▼'}</span>
                  </button>
                  {isDropdownOpen && (
                    <div className="garden-jump-year-dropdown-list" ref={listRef} role="listbox">
                      {years.map(year => (
                        <button
                          key={year}
                          type="button"
                          data-year={year}
                          className={`garden-jump-year-option ${year === selectedYear ? 'garden-jump-year-option-selected' : ''}`}
                          onClick={() => handleYearSelect(year)}
                          role="option"
                          aria-selected={year === selectedYear}
                        >
                          {year} (Age {year - birthYear})
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="garden-jump-year-actions">
                <button
                  type="button"
                  className="garden-jump-year-button garden-jump-year-button-secondary"
                  onClick={onClose}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="garden-jump-year-button"
                >
                  Jump
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JumpToYearDialog;

